import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Check, Download, Loader2, AlertTriangle, ListTodo, Wrench, StickyNote, BadgeCheck } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtDate, toISO } from "@/lib/api";
import { toast } from "sonner";

const AREAS = ["Vetrina", "Account", "Gestionale", "Prenotazioni", "Pagamenti", "Admin", "Email", "Report", "Marketing"];
const SECTIONS = [
  { type: "check", title: "Cose da controllare", icon: AlertTriangle, color: "#C026D3", bg: "bg-fuchsia-50", border: "border-fuchsia-200", fields: ["title", "date", "description"] },
  { type: "todo", title: "Cose da fare", icon: ListTodo, color: "#0D9488", bg: "bg-teal-50", border: "border-teal-200", fields: ["title", "area", "description"] },
  { type: "task", title: "Task aperti", icon: Wrench, color: "#EA580C", bg: "bg-orange-50", border: "border-orange-200", fields: ["title", "area", "description", "missing"] },
  { type: "note", title: "Note / idee", icon: StickyNote, color: "#7C3AED", bg: "bg-violet-50", border: "border-violet-200", fields: ["description"] },
  { type: "feature", title: "Funzionalità completate", icon: BadgeCheck, color: "#142B5C", bg: "bg-slate-50", border: "border-slate-200", fields: ["title", "area", "description", "date"] },
];
const LABELS = { title: "Titolo", area: "Area", description: "Descrizione", missing: "Cosa manca per proseguire", date: "Data di implementazione" };

function ItemForm({ sec, initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { title: "", area: "", description: "", missing: "", date: toISO(new Date()) });
  const id = initial?.item_id || `new-${sec.type}`;
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className={`grid gap-3 rounded-2xl border-2 border-dashed ${sec.border} bg-white p-4`} data-testid={`report-form-${id}`}>
      {sec.fields.map((k) => (
        <label key={k} className="grid gap-1 text-sm font-semibold text-[#142B5C]">
          {sec.type === "note" ? "Testo" : LABELS[k]}
          {k === "description" || k === "missing" ? (
            <Textarea required={sec.type === "note"} rows={sec.type === "note" ? 4 : 2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded-xl font-normal" data-testid={`report-${k}-input-${id}`} />
          ) : k === "area" ? (
            <><Input list="report-areas" value={f.area || ""} onChange={(e) => setF({ ...f, area: e.target.value })} className="h-11 rounded-xl font-normal" data-testid={`report-area-input-${id}`} /><datalist id="report-areas">{AREAS.map((a) => <option key={a} value={a} />)}</datalist></>
          ) : (
            <Input required={k === "title"} type={k === "date" ? "date" : "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 rounded-xl font-normal" data-testid={`report-${k}-input-${id}`} />
          )}
        </label>
      ))}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="h-11 flex-1 rounded-full border border-slate-300 font-semibold" data-testid={`report-cancel-${id}`}>Annulla</button>
        <button type="submit" className="h-11 flex-1 rounded-full font-bold text-white" style={{ background: sec.color }} data-testid={`report-save-${id}`}>Salva</button>
      </div>
    </form>
  );
}

const Confirm = ({ trigger, title, desc, onOk, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle>{desc && <AlertDialogDescription>{desc}</AlertDialogDescription>}</AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={onOk} data-testid={testid}>Conferma</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

function ItemCard({ sec, item, reload }) {
  const [edit, setEdit] = useState(false);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (edit) return <ItemForm sec={sec} initial={item} onCancel={() => setEdit(false)} onSave={(f) => run(() => api.put(`/admin/report/items/${item.item_id}`, f), "Voce aggiornata").then(() => setEdit(false))} />;
  const btn = "h-9 inline-flex items-center gap-1 rounded-full px-3 text-xs font-bold";
  return (
    <div className={`rounded-2xl border ${sec.border} bg-white p-4`} data-testid={`report-item-${item.item_id}`}>
      {item.title && sec.type !== "note" && <p className="font-semibold text-[#0B192C]">{item.title}</p>}
      <div className="mt-1 flex flex-wrap gap-2 text-xs">
        {item.area && <span className="rounded-full px-2 py-0.5 font-bold" style={{ background: `${sec.color}15`, color: sec.color }}>{item.area}</span>}
        {(sec.type === "check" || sec.type === "feature") && item.date && <span className="text-slate-500">Implementata il {fmtDate(item.date)}</span>}
      </div>
      {item.description && <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{item.description}</p>}
      {sec.type === "task" && (
        <div className="mt-3 rounded-xl border-l-4 border-orange-500 bg-orange-50 p-3 text-sm" data-testid={`report-missing-${item.item_id}`}>
          <p className="text-[11px] font-bold uppercase tracking-wider text-orange-700">Cosa manca per proseguire</p>
          <p className="mt-0.5 font-semibold text-orange-900">{item.missing || "-"}</p>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {sec.type === "check" && (
          <Confirm title="Segnare come controllata?" desc="La voce sparirà dalla dashboard e dal PDF." onOk={() => run(() => api.post(`/admin/report/items/${item.item_id}/check`), "Controllata")} testid={`confirm-check-${item.item_id}`}
            trigger={<button className={`${btn} bg-fuchsia-600 text-white`} data-testid={`report-check-${item.item_id}`}><Check className="h-4 w-4" />Controllata</button>} />
        )}
        {sec.type === "todo" && (
          <Confirm title="Segnare come completata?" desc="Diventerà una funzionalità completata con la data di oggi." onOk={() => run(() => api.post(`/admin/report/items/${item.item_id}/complete`), "Spostata nelle funzionalità")} testid={`confirm-complete-${item.item_id}`}
            trigger={<button className={`${btn} bg-teal-600 text-white`} data-testid={`report-complete-${item.item_id}`}><Check className="h-4 w-4" />Completata</button>} />
        )}
        <button onClick={() => setEdit(true)} className={`${btn} border border-slate-300 text-slate-700`} data-testid={`report-edit-${item.item_id}`}><Pencil className="h-3.5 w-3.5" />Modifica</button>
        <Confirm title="Eliminare questa voce?" onOk={() => run(() => api.delete(`/admin/report/items/${item.item_id}`), "Voce eliminata")} testid={`confirm-delete-${item.item_id}`}
          trigger={<button className={`${btn} border border-red-200 text-red-600`} data-testid={`report-delete-${item.item_id}`}><Trash2 className="h-3.5 w-3.5" />Elimina</button>} />
      </div>
    </div>
  );
}

const ageColor = (d) => {
  const days = (Date.now() - new Date(d + "T00:00:00").getTime()) / 864e5;
  return days < 30 ? "#DC2626" : days < 60 ? "#2563EB" : "#111827";
};

function Features({ sec, items, reload }) {
  const areas = [...new Set(items.map((i) => i.area || "Altro"))].sort();
  return (
    <div className="space-y-5">
      {areas.map((a) => (
        <div key={a} data-testid={`feature-area-${a}`}>
          <p className="mb-2 text-sm font-bold uppercase tracking-wider text-[#142B5C]">{a} <span className="text-slate-400">({items.filter((i) => (i.area || "Altro") === a).length})</span></p>
          <div className="grid gap-2 md:grid-cols-2">
            {items.filter((i) => (i.area || "Altro") === a).map((i) => (
              <div key={i.item_id} className="relative pl-3"><span className="absolute left-0 top-4 h-2 w-2 rounded-full" style={{ background: ageColor(i.date) }} /><ItemCard sec={sec} item={i} reload={reload} /></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Section({ sec, items, reload }) {
  const [adding, setAdding] = useState(false);
  const Icon = sec.icon;
  const create = async (f) => { try { await api.post("/admin/report/items", { ...f, type: sec.type }); toast.success("Voce aggiunta"); setAdding(false); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className={`rounded-3xl border ${sec.border} ${sec.bg} p-4 sm:p-6 ${sec.type === "check" ? "ring-2 ring-fuchsia-300" : ""}`} data-testid={`report-section-${sec.type}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xl font-bold" style={{ color: sec.color }}><Icon className="h-5 w-5" />{sec.title} <span className="rounded-full bg-white px-2.5 py-0.5 text-sm" data-testid={`report-count-${sec.type}`}>{items.length}</span></h2>
        <button onClick={() => setAdding(true)} className="h-10 inline-flex items-center gap-1 rounded-full bg-white px-4 text-sm font-bold border" style={{ color: sec.color }} data-testid={`report-add-${sec.type}`}><Plus className="h-4 w-4" />Aggiungi</button>
      </div>
      <div className="mt-4 space-y-3">
        {adding && <ItemForm sec={sec} onSave={create} onCancel={() => setAdding(false)} />}
        {!items.length && !adding && <p className="text-sm text-slate-500">Nessuna voce.</p>}
        {sec.type === "feature" ? <Features sec={sec} items={items} reload={reload} /> : (
          <div className={`grid gap-3 ${sec.type === "check" ? "md:grid-cols-2" : ""}`}>{items.map((i) => <ItemCard key={i.item_id} sec={sec} item={i} reload={reload} />)}</div>
        )}
      </div>
    </section>
  );
}

export default function ReportTab() {
  const [items, setItems] = useState(null);
  const [dl, setDl] = useState(false);
  const load = useCallback(() => api.get("/admin/report/items").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    setDl(true);
    try {
      const { data } = await api.get("/admin/report/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `HomeToB_Report_${toISO(new Date())}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore nella generazione del PDF"); } finally { setDl(false); }
  };
  if (!items) return <p className="text-slate-400">Caricamento...</p>;
  return (
    <div className="space-y-5" data-testid="report-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#142B5C] p-4 sm:p-5 text-white">
        <div><p className="font-display text-lg font-bold">Report di Avanzamento</p><p className="text-sm text-slate-300">Ciò che spunti qui sparisce anche dal PDF.</p></div>
        <button onClick={download} disabled={dl} className="h-12 inline-flex items-center gap-2 rounded-full bg-[#FF6B00] px-5 font-bold hover:bg-[#E05A00] disabled:opacity-60" data-testid="report-download-pdf-btn">
          {dl ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}Scarica Report PDF
        </button>
      </div>
      {SECTIONS.map((s) => <Section key={s.type} sec={s} items={items.filter((i) => i.type === s.type)} reload={load} />)}
    </div>
  );
}
